import { useEffect, useMemo } from "react";

type FnParam<P extends Array<any>, T> = {
  /**重试方法 */
  fn: (...args: P) => Promise<T>;
  /**重试间隔 单位ms */
  retryInterval?: number;
  /**判断是否停止重试, 默认是判断result的值来判断 */
  onStopCheck?: (result: T) => boolean;
  maxRetries?: number;
};

interface ReturnFn<P extends Array<any>, T> {
  (...args: P): Promise<T>;
  clear: () => void;
}

/**
 * 调用接口直到满足条件停止重试
 */
export function autoRetryFnWrap<P extends Array<any>, T>({
  fn,
  retryInterval = 2000,
  onStopCheck,
  maxRetries = 999,
}: FnParam<P, T>): ReturnFn<P, T> {
  async function wrapFn(...args: P) {
    let retries = 0;

    const retry = async () => {
      if (retries >= maxRetries) {
        return;
      }

      const result = await fn(...args);

      if (onStopCheck) {
        // 当有 onStopCheck， 以 onStopCheck 的返回值来判断是否停止重试
        if (onStopCheck(result)) {
          return result;
        }
      } else if (result) {
        // 当没有指定 onStopCheck， 以result是否存在来判断是否停止重试
        return result;
      }

      retries++;
      // 间隔后再调用， 类似于 sleep
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, retryInterval);

        wrapFn.clear = () => {
          clearTimeout(timer);
        };
      });

      return retry();
    };

    return retry();
  }

  wrapFn.clear = () => {};

  return wrapFn;
}

/**
 * 调用接口直到满足条件停止重试
 * hook版本: 增加了unmount会停止重试的功能
 */
export function useAutoRetryFn<P extends Array<any>, T>(params: FnParam<P, T>) {
  const fn = useMemo(() => {
    return autoRetryFnWrap(params);
  }, [params]);

  useEffect(() => {
    return () => {
      // 卸载时清除倒计时
      fn.clear();
    };
  }, [fn]);

  return fn;
}
